import JSZip from "jszip";
import type { EditorFontId } from "../editor/fonts";
import { fontStackFor } from "../editor/fonts";
import {
  isJust,
  isNothing,
  just,
  nothing,
  type Maybe,
  toMaybe,
} from "../lib/maybe";
import { isTauri } from "../lib/runtime";
import { assertNever } from "../lib/assertNever";
import { trimmedMaybe } from "../lib/trimmedString";
import { readProjectAssetDataUrl } from "../lib/tauri/assets";
import type {
  ZwChapterHeadingStyle,
  ZwCopyrightPage,
  ZwDedicationPage,
  ZwMetadata,
  ZwParagraphFormatting,
} from "../lib/zwConfig";
import {
  defaultZwCopyrightPage,
  defaultZwDedicationPage,
  defaultZwMetadata,
} from "../lib/zwConfig";
import { escapeHtmlText } from "./bookTemplate";
import { chaptersFromBlocks, type BookBlock } from "./buildBook";
import {
  chapterTitleCss,
  chapterTitleHtml,
  resolveChapterHeadingStyle,
} from "./chapterHeading";
import { buildCopyrightPageHtml } from "./copyrightPage";
import { buildDedicationPageHtml } from "./dedicationPage";
import { assetRelativeFromSrc, decodeDataUrl } from "./exportAssets";
import { mapFigureEpubHtml } from "./mapFigureHtml";
import { chapterParagraphCss } from "./paragraphStyle";

export type BuildEpubInput = {
  title: string;
  subtitle?: string;
  author: string;
  paragraphFormatting: ZwParagraphFormatting;
  chapterHeadingStyle?: ZwChapterHeadingStyle;
  copyrightPage?: ZwCopyrightPage;
  dedicationPage?: ZwDedicationPage;
  metadata?: ZwMetadata;
  bodyFontId: EditorFontId;
  headingFontId: EditorFontId;
  rootPath: string;
  /** Manuscript order: chapters and sequence map figures. */
  blocks: BookBlock[];
};

type EpubImage = {
  id: string;
  href: string;
  mediaType: string;
  bytes: Uint8Array;
};

type EpubDoc = {
  id: string;
  href: string;
  title: string;
  html: string;
};

function extForMediaType(mediaType: string): string {
  switch (mediaType) {
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/gif":
      return "gif";
    case "image/webp":
      return "webp";
    case "image/svg+xml":
      return "svg";
    case "image/avif":
      return "avif";
    default:
      return "img";
  }
}

function mediaTypeForExt(ext: string): string {
  switch (ext.toLowerCase()) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "svg":
      return "image/svg+xml";
    case "avif":
      return "image/avif";
    default:
      return "application/octet-stream";
  }
}

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** Make common HTML void tags self-closing for XHTML. A tag that already
 * ends in `/>` keeps a single slash, so running this twice stays valid XML. */
function toXhtmlFragment(html: string): string {
  return html
    .replace(
      /<(img|br|hr|meta|link|source|input)(\s[^>]*?)?(?:\s*\/)*\s*>/gi,
      "<$1$2 />",
    )
    .replace(/&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[\da-f]+);)/gi, "&amp;");
}

function wrapXhtmlDocument(options: {
  title: string;
  language: string;
  bodyClass?: string;
  bodyHtml: string;
  cssHref?: string;
}): string {
  const lang = xmlEscape(options.language !== "" ? options.language : "en");
  const title = xmlEscape(options.title);
  const bodyClass = trimmedMaybe(options.bodyClass);
  const cls = isJust(bodyClass) ? ` class="${xmlEscape(bodyClass.value)}"` : "";
  const cssHref = trimmedMaybe(options.cssHref);
  const css = isJust(cssHref)
    ? `  <link rel="stylesheet" type="text/css" href="${xmlEscape(cssHref.value)}" />\n`
    : "";
  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" lang="${lang}" xml:lang="${lang}">
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
${css}</head>
<body${cls}>
${toXhtmlFragment(options.bodyHtml)}
</body>
</html>
`;
}

function buildEpubCss(
  bodyFontId: EditorFontId,
  headingFontId: EditorFontId,
  paragraphFormatting: ZwParagraphFormatting,
  chapterHeadingStyle: ZwChapterHeadingStyle,
): string {
  const bodyFont = fontStackFor(bodyFontId);
  const headingFont = fontStackFor(headingFontId);
  return `body {
  margin: 1em;
  font-family: ${bodyFont};
  font-size: 1em;
  line-height: 1.55;
  color: #1c1917;
}

h1, h2, h3, h4, h5, h6 {
  font-family: ${headingFont};
  font-weight: 600;
  line-height: 1.25;
  page-break-after: avoid;
  break-after: avoid;
}

h1 { font-size: 1.75em; margin: 0 0 0.75em; }
h2 { font-size: 1.35em; margin: 1.4em 0 0.55em; }
h3 { font-size: 1.15em; margin: 1.2em 0 0.45em; }

p { margin: 0 0 0.85em; }

img {
  max-width: 100%;
  height: auto;
}

blockquote {
  margin: 0.9em 0;
  padding-left: 1em;
  border-left: 2px solid #d6d3d1;
  color: #44403c;
}

code, pre {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.92em;
}

pre {
  padding: 0.75em 1em;
  overflow-x: auto;
  background: #f5f5f4;
}

a { color: #57534e; }

.title-page {
  margin-top: 30%;
  text-align: center;
}

.title-page h1 {
  font-size: 2em;
  margin-bottom: 0.4em;
}

.book-subtitle {
  font-style: italic;
  margin: 0 0 1.25em;
}

.book-byline {
  margin-top: 1.5em;
  letter-spacing: 0.02em;
}

.copyright-page p,
.dedication-page p {
  margin: 0 0 0.75em;
}

.dedication-page {
  margin-top: 30%;
  text-align: center;
  font-style: italic;
}

.cover-page {
  margin: 0;
  text-align: center;
}

.cover-page img {
  max-width: 100%;
  max-height: 95vh;
  height: auto;
}

.zw-scene-break {
  text-align: center;
  margin: 1.6em 0;
  letter-spacing: 0.15em;
}

section.map-figure-epub {
  margin: 1em 0;
  text-align: center;
  page-break-before: always;
  break-before: page;
}

section.map-figure-epub img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}

${chapterTitleCss(chapterHeadingStyle)}
${chapterParagraphCss(paragraphFormatting)}
`;
}

function buildTitlePageHtml(
  title: string,
  author: string,
  subtitle?: string,
): string {
  const parts = [
    `<div class="title-page">`,
    `<h1>${escapeHtmlText(title)}</h1>`,
  ];
  const subtitleTrimmed = subtitle?.trim() ?? "";
  if (subtitleTrimmed !== "") {
    parts.push(
      `<p class="book-subtitle">${escapeHtmlText(subtitleTrimmed)}</p>`,
    );
  }
  const authorTrimmed = author.trim();
  if (authorTrimmed !== "") {
    parts.push(`<p class="book-byline">${escapeHtmlText(authorTrimmed)}</p>`);
  }
  parts.push(`</div>`);
  return parts.join("\n");
}

async function rewriteImagesForEpub(
  html: string,
  rootPath: string,
  images: EpubImage[],
  imageCache: Map<string, string>,
): Promise<string> {
  const re = /<img\s+([^>]*?)src="([^"]*)"([^>]*)>/gi;
  const matches = [...html.matchAll(re)];
  if (matches.length === 0) return html;

  let out = html;
  for (const match of matches) {
    const full = match[0]!;
    const before = match[1] ?? "";
    const src = match[2] ?? "";
    const after = match[3] ?? "";

    let cachedHref = toMaybe(imageCache.get(src));
    if (isNothing(cachedHref)) {
      let dataUrlM = src.startsWith("data:") ? just(src) : nothing;
      if (isNothing(dataUrlM)) {
        const relativeM = assetRelativeFromSrc(src);
        if (isJust(relativeM) && isTauri()) {
          try {
            dataUrlM = just(
              await readProjectAssetDataUrl(rootPath, relativeM.value),
            );
          } catch (error) {
            console.warn(
              "EPUB export: failed to embed image",
              relativeM.value,
              error,
            );
          }
        }
      }
      if (isNothing(dataUrlM)) continue;
      const decodedM = decodeDataUrl(dataUrlM.value);
      if (isNothing(decodedM)) continue;
      const decoded = decodedM.value;
      const index = images.length + 1;
      const ext = extForMediaType(decoded.mediaType);
      const id = `img-${String(index).padStart(3, "0")}`;
      const href = `images/${id}.${ext}`;
      images.push({
        id,
        href,
        mediaType: decoded.mediaType,
        bytes: decoded.bytes,
      });
      imageCache.set(src, href);
      cachedHref = just(href);
    }

    out = out.replace(
      full,
      `<img ${before}src="../${cachedHref.value}"${after} />`,
    );
  }
  return out;
}

/**
 * Build an EPUB 3 package as bytes (ZIP).
 * Reuses chapter prep / front matter from the PDF path; packages as reflowable XHTML.
 */
function epubExportTitle(input: BuildEpubInput): string {
  const trimmed = input.title.trim();
  if (trimmed !== "") return trimmed;
  const fromChapter =
    chaptersFromBlocks(input.blocks)[0]?.name.replace(
      /\.(md|markdown|mdown)$/i,
      "",
    ) ?? "";
  return fromChapter !== "" ? fromChapter : "Untitled";
}

export async function buildBookEpub(
  input: BuildEpubInput,
): Promise<Uint8Array> {
  const title = epubExportTitle(input);
  const author = input.author.trim();
  const metaTitleTrimmed = input.metadata?.title?.trim() ?? "";
  const metaAuthorTrimmed = input.metadata?.author?.trim() ?? "";
  const meta: ZwMetadata = {
    ...defaultZwMetadata(),
    ...input.metadata,
    title: metaTitleTrimmed !== "" ? metaTitleTrimmed : title,
    subtitle: input.metadata?.subtitle ?? input.subtitle ?? "",
    author: metaAuthorTrimmed !== "" ? metaAuthorTrimmed : author,
  };
  const language = meta.language.trim() !== "" ? meta.language.trim() : "en";
  const isbnTrimmed = meta.isbn.trim();
  const identifier =
    isbnTrimmed !== ""
      ? `urn:isbn:${isbnTrimmed}`
      : `urn:uuid:${crypto.randomUUID()}`;

  const images: EpubImage[] = [];
  const imageCache = new Map<string, string>();
  const docs: EpubDoc[] = [];

  // Cover image
  let coverImage: Maybe<EpubImage> = nothing;
  const coverRelative = meta.coverImage.trim();
  if (coverRelative !== "" && isTauri()) {
    try {
      const dataUrl = await readProjectAssetDataUrl(
        input.rootPath,
        coverRelative,
      );
      const decodedM = decodeDataUrl(dataUrl);
      if (isJust(decodedM)) {
        const decoded = decodedM.value;
        const extFromPath = coverRelative.split(".").pop()?.toLowerCase() ?? "";
        const ext =
          extFromPath !== "" ? extFromPath : extForMediaType(decoded.mediaType);
        const cover: EpubImage = {
          id: "cover-image",
          href: `images/cover.${ext === "jpeg" ? "jpg" : ext}`,
          mediaType:
            decoded.mediaType === "application/octet-stream"
              ? mediaTypeForExt(ext)
              : decoded.mediaType,
          bytes: decoded.bytes,
        };
        coverImage = just(cover);
        images.push(cover);
        docs.push({
          id: "cover",
          href: "text/cover.xhtml",
          title: "Cover",
          html: wrapXhtmlDocument({
            title: "Cover",
            language,
            bodyClass: "cover-page",
            cssHref: "../styles/book.css",
            bodyHtml: `<div class="cover-page"><img src="../${cover.href}" alt="${xmlEscape(title)}" /></div>`,
          }),
        });
      }
    } catch (error) {
      console.warn("EPUB export: failed to embed cover", error);
    }
  }

  docs.push({
    id: "title-page",
    href: "text/title.xhtml",
    title: "Title page",
    html: wrapXhtmlDocument({
      title,
      language,
      cssHref: "../styles/book.css",
      bodyHtml: buildTitlePageHtml(
        title,
        author,
        meta.subtitle !== "" ? meta.subtitle : input.subtitle,
      ),
    }),
  });

  const copyrightHtml = buildCopyrightPageHtml(
    input.copyrightPage ?? defaultZwCopyrightPage(),
    meta,
  );
  if (isJust(copyrightHtml)) {
    docs.push({
      id: "copyright",
      href: "text/copyright.xhtml",
      title: "Copyright",
      html: wrapXhtmlDocument({
        title: "Copyright",
        language,
        cssHref: "../styles/book.css",
        bodyHtml: copyrightHtml.value,
      }),
    });
  }

  const dedicationHtml = buildDedicationPageHtml(
    input.dedicationPage ?? defaultZwDedicationPage(),
  );
  if (isJust(dedicationHtml)) {
    docs.push({
      id: "dedication",
      href: "text/dedication.xhtml",
      title: "Dedication",
      html: wrapXhtmlDocument({
        title: "Dedication",
        language,
        cssHref: "../styles/book.css",
        bodyHtml: dedicationHtml.value,
      }),
    });
  }

  const headingStyle = resolveChapterHeadingStyle(
    toMaybe(input.chapterHeadingStyle),
  );
  let chapterOrdinal = 0;
  let mapOrdinal = 0;
  for (const block of input.blocks) {
    switch (block.type) {
      case "chapter": {
        chapterOrdinal += 1;
        const titleHtml = chapterTitleHtml(block.name, headingStyle);
        const html = await rewriteImagesForEpub(
          `${titleHtml}\n${block.html}`,
          input.rootPath,
          images,
          imageCache,
        );
        const docTitle = block.name.replace(/\n/g, ": ");
        const id = `chapter-${String(chapterOrdinal).padStart(3, "0")}`;
        docs.push({
          id,
          href: `text/${id}.xhtml`,
          title: docTitle,
          html: wrapXhtmlDocument({
            title: docTitle,
            language,
            cssHref: "../styles/book.css",
            bodyHtml: `<section class="chapter" epub:type="chapter">\n${html}\n</section>`,
          }),
        });
        break;
      }
      case "map": {
        mapOrdinal += 1;
        const html = await rewriteImagesForEpub(
          mapFigureEpubHtml(block.prepared),
          input.rootPath,
          images,
          imageCache,
        );
        const docTitle =
          block.prepared.title.trim() !== ""
            ? block.prepared.title.trim()
            : `Map ${String(mapOrdinal)}`;
        const id = `map-${String(mapOrdinal).padStart(3, "0")}`;
        docs.push({
          id,
          href: `text/${id}.xhtml`,
          title: docTitle,
          html: wrapXhtmlDocument({
            title: docTitle,
            language,
            cssHref: "../styles/book.css",
            bodyHtml: html,
          }),
        });
        break;
      }
      default:
        assertNever(block);
    }
  }

  const navItems = docs
    .filter((doc) => doc.id !== "cover")
    .map(
      (doc) =>
        `      <li><a href="${xmlEscape(doc.href)}">${xmlEscape(doc.title)}</a></li>`,
    )
    .join("\n");

  const navXhtml = wrapXhtmlDocument({
    title: "Contents",
    language,
    cssHref: "../styles/book.css",
    bodyHtml: `<nav epub:type="toc" id="toc">
  <h1>Contents</h1>
  <ol>
${navItems}
  </ol>
</nav>`,
  });

  const manifestItems = [
    `    <item id="nav" href="nav.xhtml" media-type="application/xhtml+xml" properties="nav" />`,
    `    <item id="css" href="styles/book.css" media-type="text/css" />`,
    ...docs.map(
      (doc) =>
        `    <item id="${doc.id}" href="${doc.href}" media-type="application/xhtml+xml" />`,
    ),
    ...images.map((image) => {
      const coverProp =
        image.id === "cover-image" ? ` properties="cover-image"` : "";
      return `    <item id="${image.id}" href="${image.href}" media-type="${image.mediaType}"${coverProp} />`;
    }),
  ].join("\n");

  const spineItems = docs
    .map((doc) => `    <itemref idref="${doc.id}" />`)
    .join("\n");

  const metaTags = [
    `    <dc:identifier id="book-id">${xmlEscape(identifier)}</dc:identifier>`,
    `    <dc:title>${xmlEscape(title)}</dc:title>`,
    `    <dc:language>${xmlEscape(language)}</dc:language>`,
  ];
  if (author !== "") {
    metaTags.push(`    <dc:creator>${xmlEscape(author)}</dc:creator>`);
  }
  const publisherTrimmed = meta.publisher.trim();
  if (publisherTrimmed !== "") {
    metaTags.push(
      `    <dc:publisher>${xmlEscape(publisherTrimmed)}</dc:publisher>`,
    );
  }
  const rightsTrimmed = meta.rights.trim();
  if (rightsTrimmed !== "") {
    metaTags.push(`    <dc:rights>${xmlEscape(rightsTrimmed)}</dc:rights>`);
  }
  const descriptionTrimmed = meta.description.trim();
  if (descriptionTrimmed !== "") {
    metaTags.push(
      `    <dc:description>${xmlEscape(descriptionTrimmed)}</dc:description>`,
    );
  }
  const subjectTrimmed = meta.subject.trim();
  if (subjectTrimmed !== "") {
    metaTags.push(`    <dc:subject>${xmlEscape(subjectTrimmed)}</dc:subject>`);
  }
  const pubDateTrimmed = meta.pubDate.trim();
  if (pubDateTrimmed !== "") {
    metaTags.push(`    <dc:date>${xmlEscape(pubDateTrimmed)}</dc:date>`);
  }
  if (isJust(coverImage)) {
    metaTags.push(`    <meta name="cover" content="${coverImage.value.id}" />`);
  }

  const opf = `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" unique-identifier="book-id" version="3.0" xml:lang="${xmlEscape(language)}">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
${metaTags.join("\n")}
    <meta property="dcterms:modified">${new Date().toISOString().replace(/\.\d{3}Z$/, "Z")}</meta>
  </metadata>
  <manifest>
${manifestItems}
  </manifest>
  <spine>
${spineItems}
  </spine>
</package>
`;

  const container = `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml" />
  </rootfiles>
</container>
`;

  const zip = new JSZip();
  zip.file("mimetype", "application/epub+zip", {
    compression: "STORE",
  });
  zip.folder("META-INF")!.file("container.xml", container);
  const oebps = zip.folder("OEBPS")!;
  oebps.file("content.opf", opf);
  oebps.file("nav.xhtml", navXhtml);
  oebps
    .folder("styles")!
    .file(
      "book.css",
      buildEpubCss(
        input.bodyFontId,
        input.headingFontId,
        input.paragraphFormatting,
        resolveChapterHeadingStyle(toMaybe(input.chapterHeadingStyle)),
      ),
    );
  const textFolder = oebps.folder("text")!;
  for (const doc of docs) {
    const name = doc.href.replace(/^text\//, "");
    textFolder.file(name, doc.html);
  }
  if (images.length !== 0) {
    const imageFolder = oebps.folder("images")!;
    for (const image of images) {
      const name = image.href.replace(/^images\//, "");
      imageFolder.file(name, image.bytes);
    }
  }

  return zip.generateAsync({
    type: "uint8array",
    mimeType: "application/epub+zip",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
}
